import * as Schema from "effect/Schema";

import { AssetSha256 } from "../ids.js";

/**
 * Video / audio / nav / spacer — the four widget kinds the editor serializes
 * and the renderer draws, but which the `Widget` union never declared. The
 * publish path emitted them anyway behind `as unknown as Widget["content"]`,
 * so the document written to `site.json` could not be decoded by its own
 * schema. Declaring them here is what makes decoding at publish possible.
 *
 * Defaults are NOT persisted (serializeSiteDoc only writes non-defaults), so
 * every flag below is optional.
 */

export const VideoWidgetContent = Schema.Struct({
  contentType: Schema.Literal("video"),
  asset: AssetSha256,
  mime: Schema.String,
  /** Controls are on by default; only the explicit `false` is carried. */
  controls: Schema.optional(Schema.Boolean),
  autoplay: Schema.optional(Schema.Boolean),
  loop: Schema.optional(Schema.Boolean),
  muted: Schema.optional(Schema.Boolean),
});
export type VideoWidgetContent = Schema.Schema.Type<typeof VideoWidgetContent>;

export const AudioWidgetContent = Schema.Struct({
  contentType: Schema.Literal("audio"),
  asset: AssetSha256,
  mime: Schema.String,
  /** Precomputed waveform envelope so the page draws it without decoding. */
  peaks: Schema.optional(Schema.Array(Schema.Number)),
  duration: Schema.optional(Schema.Number.pipe(Schema.positive())),
});
export type AudioWidgetContent = Schema.Schema.Type<typeof AudioWidgetContent>;

/** A marker: the renderer builds the links from the site's own pages, so
 *  there is nothing to persist beyond "a menu goes here". */
export const NavWidgetContent = Schema.Struct({
  contentType: Schema.Literal("nav"),
});
export type NavWidgetContent = Schema.Schema.Type<typeof NavWidgetContent>;

/** Pure layout whitespace — kept (rather than dropped as unknown) so its grid
 *  cell still reserves the space. */
export const SpacerWidgetContent = Schema.Struct({
  contentType: Schema.Literal("spacer"),
});
export type SpacerWidgetContent = Schema.Schema.Type<
  typeof SpacerWidgetContent
>;
